import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    GoogleMap, withGoogleMap,
    Marker, TrafficLayer, InfoWindow, DirectionsRenderer,
} from 'react-google-maps';

const availableTruck = require('../../../assets/img/freeTruck.png');
const busyTruck = require('../../../assets/img/busyTruck.png');
const RecoveryTruck = require('../../../assets/img/RecoveryTruck.png');

const AVAILABLE_STATUSES = ['available', 'Dropped', 'Cleared'];

// ── NO withScriptjs — react-streetview already loads Google Maps API ────────
// Using withScriptjs here causes a double-load with different params (v3 vs
// v3.exp, different libraries) which silently breaks the map.
const MapComponent = withGoogleMap((props) => (
    <GoogleMap
        defaultZoom={11}
        defaultCenter={{ lat: 33.429078, lng: -111.943817 }}
    >
        {props.children}
    </GoogleMap>
));

// ── Helpers ──────────────────────────────────────────────────────────────────

const parseLatlng = (str) => {
    if (!str) return null;
    const parts = str.split(',');
    const lat = parseFloat(parts[0]);
    const lng = parseFloat(parts[1]);
    return isNaN(lat) || isNaN(lng) ? null : { lat, lng };
};

const getTruckIcon = (truck) => {
    if (truck.invoiceCount >= 2) return RecoveryTruck;
    return AVAILABLE_STATUSES.includes(truck.truckStatus) ? availableTruck : busyTruck;
};

const ROUTE_OPTIONS = {
    suppressMarkers: true,
    polylineOptions: {
        strokeColor: '#2979FF',
        strokeOpacity: 0.85,
        strokeWeight: 5,
    },
};

const PICKUP_ICON_URL = 'https://maps.google.com/mapfiles/ms/icons/red-dot.png';
const DROP_ICON_URL = 'https://maps.google.com/mapfiles/ms/icons/green-dot.png';

// ── Component ───────────────────────────────────────────────────────────────

const AssignMap = ({ mapVehicleInfo = [], callData = [], dropPointers, pickUpPointers }) => {
    const [hovered, setHovered] = useState({ type: null, index: null });
    const [directions, setDirections] = useState(null);
    const [googleReady, setGoogleReady] = useState(!!window.google && !!window.google.maps);
    const prevRouteKey = useRef('');

    // ── Wait for Google Maps API (loaded by react-streetview) ────────────
    useEffect(() => {
        if (googleReady) return;
        const interval = setInterval(() => {
            if (window.google && window.google.maps) {
                setGoogleReady(true);
                clearInterval(interval);
            }
        }, 300);
        return () => clearInterval(interval);
    }, [googleReady]);

    // ── Compute driving route ────────────────────────────────────────────
    useEffect(() => {
        if (!googleReady) return;
        if (!callData || callData.length === 0) { setDirections(null); return; }
        const call = callData[0];
        if (!call) { setDirections(null); return; }

        const origin = parseLatlng(call.pickupLatlang);
        const dest = parseLatlng(call.dropLatlng);
        const originAddr = call.pickupAddress || '';
        const destAddr = call.dropoffAddress || '';

        const routeOrigin = origin || (originAddr || null);
        const routeDest = dest || (destAddr || null);
        if (!routeOrigin || !routeDest) { setDirections(null); return; }

        const key = JSON.stringify({ o: routeOrigin, d: routeDest });
        if (key === prevRouteKey.current) return;
        prevRouteKey.current = key;

        const svc = new window.google.maps.DirectionsService();
        svc.route({
            origin: typeof routeOrigin === 'string'
                ? routeOrigin
                : new window.google.maps.LatLng(routeOrigin.lat, routeOrigin.lng),
            destination: typeof routeDest === 'string'
                ? routeDest
                : new window.google.maps.LatLng(routeDest.lat, routeDest.lng),
            travelMode: window.google.maps.TravelMode.DRIVING,
        }, (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK) {
                setDirections(result);
            } else {
                console.warn('Directions request failed:', status);
            }
        });
    }, [googleReady, callData]);

    const onHover = useCallback((type, idx) => setHovered({ type, index: idx }), []);
    const onLeave = useCallback(() => setHovered({ type: null, index: null }), []);

    if (!googleReady) {
        return <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d' }}>Loading map…</div>;
    }

    return (
        <MapComponent
            containerElement={<div style={{ height: '80vh' }} />}
            mapElement={<div style={{ height: '100%' }} />}
        >
            <TrafficLayer autoUpdate />

            {/* ── Driving route (blue polyline) ───────────────────── */}
            {directions && (
                <DirectionsRenderer directions={directions} options={ROUTE_OPTIONS} />
            )}

            {/* ── Truck markers ───────────────────────────────────── */}
            {mapVehicleInfo && mapVehicleInfo.map((truck, i) => {
                if (!truck) return null;
                const lat = Number(truck.latitude || 0);
                const lng = Number(truck.longitude || 0);
                if (!lat && !lng) return null;
                return (
                    <Marker key={`t-${i}`}
                        position={{ lat, lng }}
                        options={{ icon: getTruckIcon(truck) }}
                        onMouseOver={() => onHover('truck', i)}
                        onMouseOut={onLeave}>
                        {hovered.type === 'truck' && hovered.index === i && (
                            <InfoWindow onCloseClick={onLeave}>
                                <div>
                                    <span className="map-pop-label">Location:</span>
                                    <span className="map-pop-desc">{truck.currentAddress}</span><br />
                                    <span className="map-pop-label">Truck:</span>
                                    <span className="map-pop-desc">{truck.truckId}</span><br />
                                    <span className="map-pop-label">Driver Id:</span>
                                    <span className="map-pop-desc">{truck.driverId}</span><br />
                                    <span className="map-pop-label">Status:</span>
                                    <span className="map-pop-desc">{truck.truckStatus}</span>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>
                );
            })}

            {/* ── Pickup markers (red) ────────────────────────────── */}
            {callData && callData.map((call, i) => {
                const pos = parseLatlng(call.pickupLatlang);
                if (!pos) return null;
                return (
                    <Marker key={`p-${i}`}
                        position={pos}
                        options={{ icon: PICKUP_ICON_URL }}
                        onMouseOver={() => onHover('pickup', i)}
                        onMouseOut={onLeave}>
                        {hovered.type === 'pickup' && hovered.index === i && (
                            <InfoWindow onCloseClick={onLeave}>
                                <div>
                                    <span className="map-pop-label">Invoice Id:</span>
                                    <span className="map-pop-desc">{' ' + (call.displayInvoiceId || call.invoiceId)}</span><br />
                                    <span className="map-pop-label">PickUp Location:</span>
                                    <span className="map-pop-desc">{' ' + call.pickupAddress}</span><br />
                                    <span className="map-pop-label">ETA:</span>
                                    <span className="map-pop-desc">{call.etaTime || ''}</span><br />
                                    <span className="map-pop-label">Status:</span>
                                    <span className="map-pop-desc">{' ' + call.callStatus}</span><br />
                                    <span className="map-pop-label">Priority:</span>
                                    <span className="map-pop-desc">{' ' + call.priority}</span>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>
                );
            })}

            {/* ── Drop-off markers (green) ────────────────────────── */}
            {callData && callData.map((call, i) => {
                const pos = parseLatlng(call.dropLatlng);
                if (!pos) return null;
                return (
                    <Marker key={`d-${i}`}
                        position={pos}
                        options={{ icon: DROP_ICON_URL }}
                        onMouseOver={() => onHover('drop', i)}
                        onMouseOut={onLeave}>
                        {hovered.type === 'drop' && hovered.index === i && (
                            <InfoWindow onCloseClick={onLeave}>
                                <div>
                                    <span className="map-pop-label">Invoice Id:</span>
                                    <span className="map-pop-desc">{call.displayInvoiceId || call.invoiceId}</span><br />
                                    <span className="map-pop-label">Drop Location:</span>
                                    <span className="map-pop-desc">{call.dropoffAddress}</span><br />
                                    <span className="map-pop-label">ETA:</span>
                                    <span className="map-pop-desc">{call.etaTime || ''}</span><br />
                                    <span className="map-pop-label">Status:</span>
                                    <span className="map-pop-desc">{call.callStatus}</span><br />
                                    <span className="map-pop-label">Priority:</span>
                                    <span className="map-pop-desc">{' ' + call.priority}</span>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>
                );
            })}
        </MapComponent>
    );
};

export default React.memo(AssignMap);